import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "./Admin";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCheck, Settings } from "lucide-react";

type Notif = {
  id: string; kind: string; title: string; body: string | null;
  entity_type: string | null; entity_id: string | null;
  read_at: string | null; created_at: string; metadata: any;
};

const linkFor = (n: Notif) => {
  if (n.entity_type === "booking" && n.entity_id) return `/admin/bookings/${n.entity_id}`;
  if (n.entity_type === "order" && n.entity_id) return `/admin/orders/${n.entity_id}`;
  return "#";
};

const KIND_COLORS: Record<string, string> = {
  booking_created: "default",
  payment_succeeded: "default",
  payment_failed: "destructive",
  order_paid: "default",
  refund_completed: "secondary",
  refund_failed: "destructive",
  invoice_sent: "outline",
};

const AdminNotifications = () => {
  const [rows, setRows] = useState<Notif[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "unread">("all");

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("admin_notifications" as any).select("*")
      .order("created_at", { ascending: false }).limit(200);
    setRows(((data as any[]) ?? []) as Notif[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // admin_notifications is no longer in the realtime publication for privacy; poll.
    const id = window.setInterval(() => { load(); }, 15000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { window.clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, []);

  const markAll = async () => {
    const ids = rows.filter((r) => !r.read_at).map((r) => r.id);
    if (!ids.length) return;
    await supabase.from("admin_notifications" as any).update({ read_at: new Date().toISOString() }).in("id", ids);
    load();
  };

  const filtered = filter === "unread" ? rows.filter((r) => !r.read_at) : rows;

  return (
    <AdminLayout title="NOTIFICATIONS">
      <div className="flex items-center gap-3 mb-6">
        <Button variant={filter === "all" ? "default" : "outline"} size="sm" onClick={() => setFilter("all")}>All</Button>
        <Button variant={filter === "unread" ? "default" : "outline"} size="sm" onClick={() => setFilter("unread")}>
          Unread ({rows.filter((r) => !r.read_at).length})
        </Button>
        <Button variant="outline" size="sm" onClick={markAll} className="ml-auto gap-2">
          <CheckCheck className="w-4 h-4" /> Mark all read
        </Button>
        <Button asChild variant="outline" size="sm" className="gap-2">
          <Link to="/admin/notifications/preferences"><Settings className="w-4 h-4" /> Preferences</Link>
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <p className="text-muted-foreground font-body text-sm">Nothing here.</p>
      ) : (
        <div className="bg-panel border border-border divide-y divide-border">
          {filtered.map((n) => {
            const to = linkFor(n);
            const inner = (
              <div className={`p-4 ${!n.read_at ? "bg-primary/5" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant={(KIND_COLORS[n.kind] ?? "outline") as any}>{n.kind}</Badge>
                      <span className="text-xs font-mono text-muted-foreground">{new Date(n.created_at).toLocaleString()}</span>
                    </div>
                    <p className="font-body text-sm">{n.title}</p>
                    {n.body && <p className="text-xs text-muted-foreground mt-1">{n.body}</p>}
                  </div>
                  {!n.read_at && <span className="w-2 h-2 bg-primary mt-2 shrink-0" />}
                </div>
              </div>
            );
            return to === "#"
              ? <div key={n.id}>{inner}</div>
              : <Link key={n.id} to={to} className="block hover:bg-panel/50">{inner}</Link>;
          })}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminNotifications;